import React from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Routes
import DefaultNavbar from "examples/Navigation/Navbar";
import DefaultFooter from "examples/Footers/Footer/footer";
import routes from "routes";
import footerRoutes from "footer.routes";

// Images
import ChillerHeader from "assets/images/ChillerOptimizationSystem/ChillerHeader.jpg";
import ChillerOptimizationArchitecture from "assets/images/ChillerOptimizationSystem/ChillerOptimizationArchitecture.jpg";
import Phase1Icon from "assets/images/ChillerOptimizationSystem/Phase1.svg";
import Phase2Icon from "assets/images/ChillerOptimizationSystem/Phase2.svg";
import Phase3Icon from "assets/images/ChillerOptimizationSystem/Phase3.svg";

function ChillerOptimizationSystem() {
  // Define the data for each phase
  const phases = [
    {
      id: 1,
      title: "Phase 1 – SCADA & Chiller Optimization",
      timeline: "2-3 months",
      savings: "10%-15%",
      activity: "Monitoring & optimization of the chiller system to reduce energy wastage.",
      icon: Phase1Icon,
    },
    {
      id: 2,
      title: "Phase 2 – CT & Pump VSD Control",
      timeline: "1 month",
      savings: "20%-30%",
      activity: "Installation of VSD control for cooling towers and pumps.",
      icon: Phase2Icon,
    },
    {
      id: 3,
      title: "Phase 3 – AHU Optimization",
      timeline: "1 month",
      savings: "20%-30%",
      activity: "Optimization of AHUs for efficient air circulation.",
      icon: Phase3Icon,
    },
  ];

  return (
    <>
      <DefaultNavbar routes={routes} transparent light />

      {/* Header Section */}
      <MKBox position="relative" width="100%" height="55vh" display="flex" alignItems="center" justifyContent="center">
        <img
          src={ChillerHeader}
          alt="Chiller"
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "" }}
        />
        <MKTypography
          variant="h1"
          component="div"
          color="white"
          sx={{
            position: "absolute",
            fontSize: { xs: "3rem", sm: "4rem", md: "5rem" },
            fontWeight: "bold",
            mt: 8,
            textAlign: "center",
            textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
          }}
        >
          Chiller Optimization System
        </MKTypography>
      </MKBox>

      <Card sx= {{background: "white", borderRadius: "0"}}>
        {/* Main Content Section */}
        <MKBox component="section" py={4} sx={{ mt: { xs: "1cm", md: "0.5cm" }, mx: "0.3cm" }}>
          <Container>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <MKBox mb={3}>
                  <MKTypography
                    variant="h3"
                    textAlign={{ xs: "center", md: "left" }}
                    mb={1}
                    sx={({ breakpoints, typography: { size } }) => ({
                      color: "#59535c",
                      [breakpoints.down("md")]: {
                        fontSize: size["2xl"],
                      },
                      [breakpoints.down("sm")]: {
                        fontSize: size["xl"],
                      },
                      maxWidth: "100%",
                    })}
                  >
                    About the Chiller Optimization Solution
                  </MKTypography>
                  <MKTypography
                    variant="body2"
                    color="#59535c"
                    sx={{
                      fontSize: "1.2rem",
                      lineHeight: "1.75",
                      textAlign: { xs: "justify", md: "left" },
                      mt: 0,
                    }}
                  >
                    Our <strong>Chiller Optimization Solution</strong> is an AI-powered, autonomous energy management system
                    designed to enhance cooling efficiency, reduce operational costs, and contribute to net-zero energy
                    buildings. By integrating real-time sensor data, AI-driven analytics, and smart automation, the system
                    continuously optimizes chiller, pump, cooling tower, and air handling unit (AHU) operations.
                  </MKTypography>
                </MKBox>
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* How It Works Section */}
        <MKBox component="section" py={4} sx={{ mx: "0.3cm" }}>
          <Container>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12}>
                <MKBox mb={3}>
                  <MKTypography
                    variant="h3"
                    textAlign={{ xs: "center", md: "left" }}
                    mb={1}
                    sx={({ breakpoints, typography: { size } }) => ({
                      color: "#59535c",
                      [breakpoints.down("md")]: {
                        fontSize: size["2xl"],
                      },
                      [breakpoints.down("sm")]: {
                        fontSize: size["xl"],
                      },
                      maxWidth: "100%",
                    })}
                  >
                    How It Works
                  </MKTypography>
                  <MKTypography
                    variant="body2"
                    color="#333"
                    sx={{
                      fontSize: "1.1rem",
                      lineHeight: "1.75",
                      textAlign: { xs: "justify", md: "left" },
                    }}
                  >
                    <strong>Real-Time Monitoring & Data Acquisition:</strong> IoT sensors collect crucial data—including
                    humidity, temperature, power consumption, chilled water supply & return temperatures, and flow
                    rates—which is transmitted via the Mi-SPARK Gateway to the cloud for analysis.
                    <br /><br />
                    <strong>AI-Driven Analysis & Optimization:</strong> The SquareCloud AI Engine processes real-time data to
                    predict optimal chiller operations, adjust power loads dynamically, and autonomously optimize energy
                    usage based on demand. The SCADA dashboard provides insights, alerts, and reporting for energy
                    efficiency improvements.
                    <br /><br />
                    <strong>Automated Control & Energy Efficiency Measures:</strong> With Variable Speed Drive (VSD)
                    integration for cooling towers, pumps, and AHUs, the system adjusts motor speeds based on real-time
                    demand, ensuring efficiency. Automated AHU optimization ensures coordinated chiller and air
                    distribution performance.
                  </MKTypography>
                </MKBox>
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Architecture Section */}
        <MKBox component="section" py={4} sx={{ mx: "0.3cm" }}>
          <Container>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <MKTypography
                  variant="h3"
                  textAlign="center"
                  mb={1}
                  sx={({ breakpoints, typography: { size } }) => ({
                    color: "#59535c",
                    [breakpoints.down("md")]: {
                      fontSize: size["2xl"],
                    },
                    [breakpoints.down("sm")]: {
                      fontSize: size["xl"],
                    },
                    maxWidth: "100%",
                  })}
                >
                  Chiller Optimization Solution Architecture
                </MKTypography>
                <MKBox textAlign="center" mb={4}>
                  <img
                    src={ChillerOptimizationArchitecture}
                    alt="Chiller Optimization Architecture"
                    style={{ width: "80%", borderRadius: "8px", boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.2)" }}
                  />
                </MKBox>
                <MKTypography
                  variant="body2"
                  color="#333"
                  textAlign={{ xs: "justify", md: "center" }}
                  sx={{
                    fontSize: "1.1rem",
                    lineHeight: "1.75",
                    maxWidth: "80%",
                    mx: "auto",
                  }}
                >
                  The <strong>Chiller Optimization Solution</strong> follows a multi-layered architecture integrating IoT
                  sensors, cloud computing, and AI-powered automation. <strong>IoT Sensors & Edge Devices</strong> continuously
                  monitor real-time environmental and operational parameters. The <strong>Mi-SPARK Gateway</strong> securely
                  transmits this data to the cloud for further analysis. Through <strong>Cloud AI & Data Processing</strong>,
                  the AI engine predicts and optimizes chiller operations dynamically. The <strong>SCADA Dashboard</strong> provides
                  real-time monitoring, control, and visualization of system performance. Lastly, <strong>VSD & AHU Optimization</strong> enhances
                  cooling efficiency by dynamically adjusting motor speeds.
                </MKTypography>
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Implementation Phases Section */}
        <MKBox component="section" py={6} sx={{ mx: "0.3cm" }}>
          <Container>
            <MKTypography
              variant="h3"
              textAlign="center"
              mb={2}
              sx={({ breakpoints, typography: { size } }) => ({
                color: "#59535c",
                [breakpoints.down("md")]: {
                  fontSize: size["2xl"],
                },
                [breakpoints.down("sm")]: {
                  fontSize: size["xl"],
                },
                maxWidth: "100%",
              })}
            >
              Energy Saving Measures by Phases
            </MKTypography>
            <Grid container spacing={4} justifyContent="center">
              {phases.map((phase) => (
                <Grid item xs={12} sm={6} md={4} key={phase.id}>
                  <Card
                    sx={{
                      height: "100%",
                      boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.1)",
                      borderRadius: "12px",
                      textAlign: "center",
                      p: 3,
                    }}
                  >
                    <MKBox display="flex" justifyContent="center" mb={2}>
                      {/* Centered Icon with Gray Border */}
                      <MKBox
                        sx={{
                          width: { xs: "70px", md: "90px" },
                          height: { xs: "70px", md: "90px" },
                          borderRadius: "50%",
                          border: "4px solid #ccc",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#f9f9f9",
                        }}
                      >
                        <img src={phase.icon} alt={`Phase ${phase.id} Icon`} width="50px" />
                      </MKBox>
                    </MKBox>
                    <CardContent sx={{ p: 0 }}>
                      <MKTypography
                        variant="body2"
                        sx={{
                          color: "#59535c",
                          fontWeight: "bold",
                          fontSize: "1.1rem",
                          lineHeight: "1.75",
                          textAlign: "center",
                        }}
                        mb={1}
                      >
                        {phase.title}
                      </MKTypography>
                      <MKTypography
                        variant="body2"
                        sx={{
                          fontSize: "1rem",
                          lineHeight: "1.75",
                          textAlign: "center",
                        }}
                      >
                        <strong>Implementation Timeline:</strong> {phase.timeline}
                        <br />
                        <strong>Energy Savings:</strong> {phase.savings}
                        <br />
                        <strong>Key Activity:</strong> {phase.activity}
                      </MKTypography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>
</Card>

      <MKBox pt={4} px={0} mt={-5}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default ChillerOptimizationSystem;
